import CATALOG from '@/generated/design-packs-catalog.json';

/** The looks a video from the landing page can start in: the ones whose first video fits the free credits (no generated footage). */
const IDS = ['drawn-explainer', 'nutshell-world', 'hero-object', 'living-diagram', 'annotated-evidence', 'story-scene'] as const;
export type LookId = (typeof IDS)[number];

export interface Look {
  id: LookId;
  name: string;
}

// A pack missing from the catalog is left out, never a crash: the home screen loads this module in every build.
export const LOOKS: Look[] = IDS.flatMap((id) => {
  const pack = (CATALOG as { id: string; name: string }[]).find((p) => p.id === id);
  return pack ? [{ id, name: pack.name }] : [];
});

export const lookName = (id: string | null | undefined): string | undefined => LOOKS.find((l) => l.id === id)?.name;

/** The look to name before Ivy has planned: only one the person picked. A look their words name is a lean, and Ivy settles it at the first stop. */
export const pickedLookName = (look: { id: string | null; from: 'picked' | 'words' } | null | undefined): string | undefined =>
  look?.from === 'picked' ? lookName(look.id) : undefined;

const NAMED: [RegExp, LookId][] = [
  [/\bhand[- ]?drawn\b|\bwhiteboard\b|\bsketch(?:ed|y|-style)?\b|\bdoodles?\b|\bpencil\b|\bchalkboard\b/i, 'drawn-explainer'],
  [/\billustrated\b|\billustrations?\b|\bkurzgesagt\b|\bpaper[- ]?cut(?:out)?\b|\bflat (?:design|style)\b/i, 'nutshell-world'],
  [/\bstudio object\b|\bproduct (?:shot|video|reveal)\b|\bhero shot\b|\bturntable\b|\b3d\b/i, 'hero-object'],
  [/\bdiagram board\b|\bdiagrams?\b|\bflow ?charts?\b|\bschematics?\b|\bblueprint\b/i, 'living-diagram'],
  [/\bevidence desk\b|\bcase file\b|\bevidence board\b|\btrue crime\b|\bdetective\b|\binvestigat(?:ive|ion)\b/i, 'annotated-evidence'],
  [/\bcharacter stage\b|\bcharacters?\b|\bcartoon people\b/i, 'story-scene'],
];

const OTHER_STYLE = /\bcinematic\b|\bdocumentary\b|\banime\b|\bmanga\b|\bcomic(?: book)?\b|\bwatercolou?r\b|\bpixel art\b|\bclaymation\b|\bstop[- ]motion\b|\bin the style of\b/i;

export type NamedLook = { kind: 'look'; id: LookId } | { kind: 'words' };

/**
 * A look the words name, from a local word list, so /start can show it at once without asking the server. A style
 * we don't offer here is `words`: the look step is skipped and Ivy reads it from the prompt.
 */
export function lookFromPrompt(prompt: string): NamedLook | null {
  let best: { id: LookId; at: number } | null = null;
  for (const [pattern, id] of NAMED) {
    const at = prompt.search(pattern);
    if (at >= 0 && (!best || at < best.at)) best = { id, at };
  }
  if (best) return { kind: 'look', id: best.id };
  return OTHER_STYLE.test(prompt) ? { kind: 'words' } : null;
}
